import api from '@/lib/api';
import type { PaginatedResponse } from '@pms/shared';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface EmailConfig {
  id: string;
  propertyId: string;
  provider: 'sendgrid' | 'smtp';
  senderEmail: string;
  senderName: string;
  isActive: boolean;
  smtpHost?: string | null;
  smtpPort?: number | null;
}

export interface EmailLog {
  id: string;
  guestId: string | null;
  bookingId: string | null;
  templateType: string;
  subject: string;
  recipientEmail: string;
  status: 'queued' | 'sent' | 'delivered' | 'bounced' | 'failed' | 'opened';
  sentAt: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
  guest?: { id: string; fullName: string } | null;
}

export interface EmailLogsQuery {
  page?: number;
  limit?: number;
  status?: string;
  from?: string;
  to?: string;
}

export interface UpdateEmailConfigPayload {
  provider: 'sendgrid' | 'smtp';
  apiKey?: string | undefined;
  smtpHost?: string | undefined;
  smtpPort?: number | undefined;
  smtpUser?: string | undefined;
  smtpPass?: string | undefined;
  senderEmail: string;
  senderName: string;
}

export interface SendEmailPayload {
  guestId: string;
  bookingId?: string | undefined;
  templateType: string;
  overrideSubject?: string | undefined;
  overrideBody?: string | undefined;
}

// ─── API calls ──────────────────────────────────────────────────────────────

export async function fetchEmailConfig(propertyId: string): Promise<EmailConfig | null> {
  const { data } = await api.get('/notifications/email-config', { params: { propertyId } });
  return data;
}

export async function saveEmailConfig(
  propertyId: string,
  payload: UpdateEmailConfigPayload,
): Promise<EmailConfig> {
  const { data } = await api.put('/notifications/email-config', payload, {
    params: { propertyId },
  });
  return data;
}

export async function testEmailConfig(
  propertyId: string,
): Promise<{ success: boolean; message: string }> {
  const { data } = await api.post(
    '/notifications/email-config/test',
    {},
    { params: { propertyId } },
  );
  return data;
}

export async function fetchEmailLogs(
  query: EmailLogsQuery,
): Promise<PaginatedResponse<EmailLog>> {
  const { data } = await api.get<PaginatedResponse<EmailLog>>('/notifications/email-logs', {
    params: query,
  });
  return data;
}

export async function sendEmail(payload: SendEmailPayload): Promise<EmailLog> {
  const { data } = await api.post('/notifications/send', payload);
  return data;
}
